// import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import { useCallback, useEffect, useRef, useState } from "react";
import { handleError } from "utils";
import constants from "../../constants";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";

const allowedTypes = constants.chat.allowedTypesForMessage;
const fileSizeLimitInMbs = 25;
const fileSizeLimitInBytes = fileSizeLimitInMbs * 1024 * 1024;
const enterCode = "Enter";
const newLineKey = "\n";

const useCustomMessageInputState = (dependencies) => {
  const { currentChannel, sendUserMessage, sendFileMessage, t } = dependencies;

  const context = useGroupChannelContext();

  const isChatClosed =
    currentChannel.customType === constants.chat.patient.close;
  const {
    onBeforeSendUserMessage,
    onBeforeSendFileMessage,
    messagesDispatcher,
    messageActionTypes,
    scrollToBottom,
    scrollRef,
  } = context;

  const scrollChatToBottom = useCallback(() => {
    const runScroll = () => {
      if (typeof scrollToBottom === "function") {
        scrollToBottom();
        return;
      }
      const scrollEl = scrollRef?.current;
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    };
    setTimeout(runScroll, 0);
    requestAnimationFrame(() => {
      requestAnimationFrame(runScroll);
    });
  }, [scrollToBottom, scrollRef]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const textareaRef = useRef();
  const [height, setHeight] = useState(60);

  const handleTextInput = useCallback((e) => {
    let updateHeight = 60;
    if (e.target.scrollHeight > updateHeight && e?.target?.value) {
      updateHeight = e.target.scrollHeight;
    }

    setHeight(updateHeight);
    const value = e.target.value;
    error && setError("");
    setText(value);
  }, [error]);

  const handleMessageSendError = useCallback(() => {
    handleError(new Error(t("messenger.sdkErrorMessage")));
  }, [t]);

  const handleSend = useCallback(async () => {
    if (!text?.trim()) {
      setText("");
      return;
    }
    const userMessageParams = onBeforeSendUserMessage(text?.trim());
    try {
      const messageHandler = await sendUserMessage(
        currentChannel,
        userMessageParams
      );
      scrollChatToBottom();
      messageHandler.onSucceeded(() => {
        setText("");
        scrollChatToBottom();
      });
      messageHandler.onFailed((err, message) => {
        const { reqId } = message;
        messagesDispatcher({
          type: messageActionTypes.ON_MESSAGE_DELETED_BY_REQ_ID,
          payload: reqId,
        });
        handleMessageSendError();
      });
    } catch (sendbirdError) {
      handleError(sendbirdError);
    }
  }, [
    currentChannel,
    handleMessageSendError,
    messageActionTypes,
    messagesDispatcher,
    onBeforeSendUserMessage,
    scrollChatToBottom,
    sendUserMessage,
    text,
  ]);

  const handleAttachment = useCallback(async (e) => {
    error && setError("");
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      if (allowedTypes.includes(file.type)) {
        if (file.size > fileSizeLimitInBytes) {
          setError(
            (err) =>
              `${err ? `${err}, ` : ""}${t("messenger.fileSizeError", {
                name: file.name,
                size: fileSizeLimitInMbs,
              })}`
          );
          continue;
        }
        const fileMessageParams = onBeforeSendFileMessage(file);
        try {
          const messageHandler = await sendFileMessage(
            currentChannel,
            fileMessageParams
          );
          scrollChatToBottom();
          messageHandler.onSucceeded(() => {
            setText("");
            scrollChatToBottom();
          });
          messageHandler.onFailed((err, message) => {
            const { reqId } = message;
            messagesDispatcher({
              type: messageActionTypes.ON_MESSAGE_DELETED_BY_REQ_ID,
              payload: reqId,
            });
            handleMessageSendError();
          });
        } catch (sendbirdError) {
          handleError(sendbirdError);
        }
      } else {
        setError(
          (err) =>
            `${err ? `${err}, ` : ""}${t("messenger.fileTypeNotSupported", {
              name: file.name,
              type: file.type,
            })}`
        );
      }
    }
    e.target.value = null;
  }, [
    currentChannel,
    error,
    handleMessageSendError,
    messageActionTypes,
    messagesDispatcher,
    onBeforeSendFileMessage,
    scrollChatToBottom,
    sendFileMessage,
    t,
  ]);

  const handleKeyPress = useCallback((e) => {
    if (textareaRef?.current) {
      if (textareaRef.current === document.activeElement) {
        const {
          key,
          shiftKey,
          // code, ctrlKey
        } = e;
        // ctrlKey &&
        // code === enterCode &&
        if (!shiftKey && (key === newLineKey || key === enterCode)) {
          handleSend();
        }
      }
    }
  }, [handleSend]);

  useEffect(() => {
    window.addEventListener("keypress", handleKeyPress);
    return () => window.removeEventListener("keypress", handleKeyPress);
  }, [handleKeyPress]);

  return {
    state: {
      text,
      error,
      textareaRef,
      height,
      isChatClosed,
    },
    updateMethods: {
      setError,
      setText,
    },
    otherMethods: {
      handleTextInput,
      handleSend,
      handleAttachment,
    },
  };
};

export default useCustomMessageInputState;
